import { ref } from 'vue';
import type { IItem } from '@/interface/IItem';
import { Fairmode } from './SettingService';

export const GroupLabel = ref<string>();
export const GroupLabels = ref<string[]>([]);
export const Items = ref<IItem[]>([]);

const API = '/api/api.php';

export class ItemService {
  private _firstItem: IItem | undefined;

  async init() {
    await this.resetGroupLabel();
    Items.value = await this.getItems();
  }

  // =========================
  // API Helpers
  // =========================
  private async fetchItems(): Promise<IItem[]> {
    const res = await fetch(API);
    return await res.json();
  }

  // =========================
  // Core Methods
  // =========================

  public getItemCount = async () => (await this.fetchItems()).length;

  public getGroupLabels = async (): Promise<string[]> => {
    const items = await this.fetchItems();
    return [
      ...new Set(
        items
          .map((i) => i.group)
          .filter(Boolean)
          .sort()
      )
    ];
  };

  public getItems = async (): Promise<IItem[]> => {
    const group = GroupLabel.value || (await this.getFirstItem()).group;
    return this.getItemByGroupLabel(group);
  };

  public getItemByGroupLabel = async (groupLabel: string): Promise<IItem[]> => {
    const items = await this.fetchItems();

    return items
      .filter((i) => i.group === groupLabel)
      .sort((a, b) => a.order - b.order)
      .map((i) => {
        const copy = { ...i };
        if (Fairmode.value) {
          copy.weight = 1;
        }
        return copy;
      });
  };

  private syncGroups = async () => (GroupLabels.value = await this.getGroupLabels());
  public syncItems = async () => (Items.value = await this.getItems());

  // =========================
  // Add
  // =========================

  public addItem = async (item?: IItem) => {
    if (!item) {
      item = {
        label: 'New Item',
        weight: 1
      } as IItem;
    }

    await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        label: item.label,
        weight: item.weight
      })
    });

    await this.syncItems();
  };

  public addItems = async (items: IItem[]) => {
    await fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        items.map((i) => ({
          label: i.label,
          weight: i.weight
        }))
      )
    });

    await this.syncItems();
  };

  // =========================
  // First Item
  // =========================

  public async getFirstItem(): Promise<IItem> {
    if (this._firstItem) return this._firstItem;

    const items = await this.fetchItems();

    if (!items.length) {
      throw new Error('No items found (check backend seeding)');
    }

    this._firstItem = items.sort((a, b) => a.order - b.order)[0];
    return this._firstItem;
  }

  // =========================
  // Group Handling
  // =========================

  public changeGroupLabel = async (newGroupLabel: string) => {
    if (newGroupLabel !== GroupLabel.value) {
      GroupLabel.value = newGroupLabel;
    }
    await this.syncItems();
  };

  public async resetGroupLabel() {
    const firstItem = await this.getFirstItem();
    GroupLabel.value = firstItem.group;
    await this.syncGroups();
    await this.syncItems();
  }

  public async renameGroup(oldGroupLabel: string, newGroupLabel: string) {
    const items = await this.fetchItems();

    const updated = items.map((i) => {
      if (i.group === oldGroupLabel) {
        return { ...i, group: newGroupLabel };
      }
      return i;
    });

    await fetch(API, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated)
    });

    await this.syncGroups();
    await this.changeGroupLabel(newGroupLabel);
  }

  // =========================
  // Update
  // =========================

  public async updateItem(item: IItem) {
    await fetch(API, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.id,
        label: item.label,
        weight: item.weight
      })
    });

    await this.syncItems();
  }

  // =========================
  // Delete
  // =========================

  public removeItem = async (item: IItem) => {
    await fetch(API, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.id })
    });

    await this.syncItems();
  };

  public removeGroup = async (groupLabel: string) => {
    const items = await this.fetchItems();

    const filtered = items.filter((i) => i.group !== groupLabel);

    await fetch(API, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filtered)
    });

    await this.resetGroupLabel();
  };

  public cleanUpGroup = async (groupLabel: string) => {
    await this.removeGroup(groupLabel);
  };
}
